/* eslint-disable react/require-default-props,react/destructuring-assignment,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesome } from '../FontAwesome';
import './ImagePreviewDropZone.scss';

const MAX_IMAGE_RETRIES = 15;

export class ImagePreviewDropZone extends React.Component {
  static propTypes = {
    image: PropTypes.string,
    upload: PropTypes.shape({
      preview: PropTypes.string,
      progress: PropTypes.number,
    }),
    onUploadClick: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
    onDragOver: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    suggestedSize: PropTypes.shape({
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      // assuming this will be in pixels
    }),
  };

  static defaultProps = {
    suggestedSize: {
      width: 400,
      height: 300,
    },
  };

  state = {
    imageRetries: 0,
    imageReady: false,
    imageReadyDone: false,
    previewReady: false,
    uploadReady: false,
    imageReadyTimeout: null,
    styles: {
      content: {},
      preview: {},
      image: {},
    },
  };

  componentWillMount() {
    const { suggestedSize } = this.props;
    const { styles } = this.state;

    const minWidth = 300;
    const maxWidth = '100%';
    const minHeight = 164;
    const maxHeight = 300;
    styles.content.maxWidth = maxWidth;
    styles.content.maxHeight = `${maxHeight + 14}px`; // to account for padding and borders;

    const contentWidth = Math.max(minWidth, suggestedSize.width);
    const contentHeight = Math.min(
      maxHeight,
      Math.max(minHeight, suggestedSize.height)
    );

    styles.content.width = `${contentWidth + 14}px`; // to account for padding and borders;
    styles.content.height = `${contentHeight + 14}px`; // to account for padding and borders;

    styles.preview.maxHeight = `${contentHeight}px`;

    styles.image = {
      maxWidth: `${contentWidth}px`,
      height: 'auto',
      maxHeight: `${contentHeight}px`,
      width: 'auto',
    };
  }

  componentWillReceiveProps(nextProps) {
    const { upload } = this.props;
    let { previewReady, uploadReady } = this.state;

    if (nextProps.image !== this.props.image) {
      // Trying to load a new image, reset the retries count.
      this.setState(() => ({ imageRetries: 0 }));
    }

    if (!nextProps.image) {
      if (this.state.imageReadyTimeout) {
        clearTimeout(this.state.imageReadyTimeout);
      }
      this.setState({
        imageReady: false,
        imageReadyTimeout: null,
        imageReadyDone: false,
      });
    }
    if (upload && upload.preview && upload.preview === nextProps.image) {
      this.onImageLoad();
    }
    if (!nextProps.upload) {
      previewReady = false;
      uploadReady = false;
    } else if (nextProps.upload.progress === 100 && upload.progress !== 100) {
      uploadReady = true;
    }
    this.setState({ previewReady, uploadReady });
  }

  componentWillUnmount() {
    if (this.state.imageReadyTimeout) {
      clearTimeout(this.state.imageReadyTimeout);
    }
  }

  setImageReadyDone = () => {
    this.setState({ imageReadyDone: true });
  };

  onPreviewImageLoad = () => {
    this.setState({ previewReady: true });
  };

  onImageLoad = () => {
    const imageReadyTimeout = setTimeout(this.setImageReadyDone, 500);

    this.setState({
      imageReady: true,
      imageReadyDone: false,
      imageReadyTimeout,
    });
  };

  onImageLoadError = () => {
    this.setState(({ imageRetries }) => {
      if (imageRetries < MAX_IMAGE_RETRIES) {
        // If we failed to load the image, try again after 1 second
        setTimeout(() => {
          this.setState((state) => ({
            imageRetries: state.imageRetries + 1,
          }));
        }, 2000);
        return {};
      }
      return {
        imageReady: true,
        imageReadyDone: true,
      };
    });
  };

  onClear = (event) => {
    event.stopPropagation();
    this.props.onClear();
  };

  renderImage = () => {
    const { image, onUploadClick, onDragOver, onDrop } = this.props;
    const { imageReady, imageReadyDone, styles, imageRetries } = this.state;
    return (
      <button
        type="button"
        className="finalized"
        onClick={onUploadClick}
        {...{ onDrop, onDragOver }}
      >
        <div
          className={classNames('preview', { imageReady, imageReadyDone })}
          style={styles.preview}
        >
          <img
            className=""
            alt=""
            src={`${image}?retries=${imageRetries}`}
            onLoad={this.onImageLoad}
            onError={this.onImageLoadError}
            style={styles.image}
          />
          <div className="image-loading fill-parent">
            <FontAwesome name="spinner" size="2x" fixedWidth pulse />
          </div>
          <div className="cancel" onClick={this.onClear}>
            <FontAwesome name="times" />
          </div>
        </div>
      </button>
    );
  };

  renderPreview = () => {
    const { upload } = this.props;
    const { previewReady, styles } = this.state;

    // reset transition animation at the start of an upload
    const transition = upload && upload.progress ? '500ms left linear' : 'none';

    return (
      <div
        className={classNames('preview', {
          previewReady,
          saving: upload.progress === 100,
        })}
      >
        <img
          alt=""
          className="preview"
          src={upload.preview}
          onLoad={this.onPreviewImageLoad}
          style={styles.image}
        />
        <div
          className="upload-progress fill-parent"
          style={{ left: `${upload.progress}%`, transition }}
        >
          <div
            className="upload-text fill-parent"
            style={{ left: `-${upload.progress}%`, transition }}
          >
            Uploading...
          </div>
        </div>
        <div className="image-loading fill-parent">
          <FontAwesome name="spinner" size="2x" fixedWidth pulse />
        </div>
      </div>
    );
  };

  render() {
    const { image, upload, onUploadClick, onDragOver, onDrop } = this.props;
    const { styles, imageRetries, imageReady } = this.state;

    return (
      <image-preview-drop-zone>
        <div className="content" style={styles.content}>
          {image ? (
            imageReady && imageRetries >= MAX_IMAGE_RETRIES ? (
              <button
                type="button"
                className="dropzone"
                onClick={onUploadClick}
                {...{ onDrop, onDragOver }}
              >
                <span className="droptext uploadError">
                  <span>There was an error during file upload</span>
                  <br />
                  Drop image or click here to try again
                </span>
              </button>
            ) : (
              this.renderImage()
            )
          ) : (
            <button
              type="button"
              className="dropzone"
              onClick={onUploadClick}
              {...{ onDrop, onDragOver }}
            >
              {upload ? (
                this.renderPreview()
              ) : (
                <span className="droptext">
                  <span>
                    <FontAwesome name="cloud-upload" /> Upload an image
                  </span>
                </span>
              )}
            </button>
          )}
        </div>
      </image-preview-drop-zone>
    );
  }
}
